import React, { createContext, useContext, useReducer, useCallback } from 'react'

// Action types
const ADD_NOTIFICATION = 'ADD_NOTIFICATION'
const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION'
const CLEAR_ALL_NOTIFICATIONS = 'CLEAR_ALL_NOTIFICATIONS'

// Initial state
const initialState = {
  notifications: []
}

// Reducer
const notificationReducer = (state, action) => {
  switch (action.type) {
    case ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, action.payload]
      }
    case REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(notification => notification.id !== action.payload)
      }
    case CLEAR_ALL_NOTIFICATIONS:
      return {
        ...state,
        notifications: []
      }
    default:
      return state
  }
}

// Context
const NotificationContext = createContext()

// Provider component
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState)

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random()
    const newNotification = {
      id,
      type: 'info',
      message: '',
      autoHideDuration: 6000,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      ...notification
    }
    
    dispatch({
      type: ADD_NOTIFICATION,
      payload: newNotification
    })

    // Auto-remove notification after specified duration
    if (newNotification.autoHideDuration > 0) {
      setTimeout(() => {
        dispatch({
          type: REMOVE_NOTIFICATION,
          payload: id
        })
      }, newNotification.autoHideDuration)
    }

    return id
  }, [])

  const removeNotification = useCallback((id) => {
    dispatch({
      type: REMOVE_NOTIFICATION,
      payload: id
    })
  }, [])

  const clearAllNotifications = useCallback(() => {
    dispatch({
      type: CLEAR_ALL_NOTIFICATIONS
    })
  }, [])

  // Convenience methods for different notification types
  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({
      type: 'success',
      message,
      ...options
    })
  }, [addNotification])

  const showError = useCallback((message, options = {}) => {
    return addNotification({
      type: 'error',
      message,
      autoHideDuration: 8000,
      ...options
    })
  }, [addNotification])

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({
      type: 'warning',
      message,
      ...options
    })
  }, [addNotification])

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({
      type: 'info',
      message,
      ...options
    })
  }, [addNotification])

  const value = {
    notifications: state.notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

// Custom hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

// Global notification manager for use outside React components
class NotificationManager {
  static instance = null
  
  constructor() {
    if (NotificationManager.instance) {
      return NotificationManager.instance
    }
    
    this.listeners = []
    NotificationManager.instance = this
  }

  subscribe(listener) {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  // Emit notification
  emit(notification) {
    this.listeners.forEach(listener => listener(notification))
  }

  // Convenience methods
  success(message, options = {}) {
    this.emit({ 
      type: 'success', 
      message, 
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      ...options 
    })
  }

  error(message, options = {}) {
    this.emit({ 
      type: 'error', 
      message, 
      autoHideDuration: 8000,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      ...options 
    })
  }

  warning(message, options = {}) {
    this.emit({ 
      type: 'warning', 
      message,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      ...options 
    })
  }

  info(message, options = {}) {
    this.emit({ 
      type: 'info', 
      message,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      ...options 
    })
  }
}

// Export singleton instance
export const notificationManager = new NotificationManager() 